/**
 * Component for Banner
 *
 * @module ui/modules/Banner
 * @memberof -Common
 */
import './Banner.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { BannerMedia as PalBannerMedia } from 'web-palette/dist/components/BannerMedia/BannerMedia';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getExternalAction } from '@ulta/core/utils/getExternalAction/getExternalAction';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

/**
 * Represents a Banner component
 *
 * @method
 * @param { BannerProps} props - React properties passed from composition
 * @returns Banner
 */
export const Banner = function( props ){
  const { breakpoint } = useDeviceInflection();
  const bannerRef = useRef( null );
  const {
    actionGroups,
    alignToGrid,
    body,
    bulletedList,
    eyebrow,
    headline,
    headlineTag = defaultProps.headlineTag,
    media,
    tag,
    textColor,
    textPosition = defaultProps.textPosition,
    variant
  } = props;

  const { large, medium, small } = media || {};
  const { actions = [], style } = actionGroups || {};

  let bannerContent;

  /** Getting each Banner variant to set which one to display in the return */
  const isBannerMedia = variant === 'media';

  /** Objects for external media and actions coming from Amplience */
  let formattedMedia;
  let formattedAction;
  let formattedActionFullyClick;

  /** Breakpoint settings for incoming media */
  const isMobile = breakpoint?.isSmallDevice();
  const isDesktop = breakpoint.isLargeDevice();
  const isTablet = breakpoint.isMediumDevice();

  /** Action check for formatters */
  const showActionGroups = actionGroups && actions[0]?.label;
  const hasAction = showActionGroups ;

  /** This checks if there is 1 action and sets the fullyClickableParent to true if so */
  const fullyClickableParent = showActionGroups && actions.length === 1;
  const isKnockoutLink = ( textColor === 'content-knockout' );
  const alignment = ( textPosition === 'center' ) ? 'center' : 'left';

  /** Image check for formatters */
  const hasImage = ( large || small || medium );

  /** Formatters for incoming media and actions */
  if( hasImage ){
    formattedMedia = getUltaAssetHtml( { image: large, mobileImage: small, tabletImage: medium, isMobile: isMobile, isDesktop: isDesktop, isTablet: isTablet } );
  }

  if( hasAction ){
    formattedAction = getExternalAction( { actionGroups: actionGroups, isKnockoutLink: isKnockoutLink, alignment: alignment } );
  }

  if( fullyClickableParent ){
    formattedActionFullyClick = actionFormatter(
      { action: actions[0], style: style, fullyClickableParent: fullyClickableParent },
      { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  useIntersectionProcessor( {
    ...props,
    componentRef: bannerRef
  } );

  // Early return
  if( !isBannerMedia ){
    return null;
  }

  if( isBannerMedia ){
    bannerContent = (
      <PalBannerMedia
        body={ body }
        bulletedList={ bulletedList }
        externalAction={ formattedAction }
        externalActionFullyClick={ formattedActionFullyClick }
        externalMedia={ formattedMedia }
        eyebrow={ eyebrow }
        fullyClickableParent={ fullyClickableParent }
        headline={ headline }
        headlineTag={ headlineTag }
        tag={ tag }
        textColor={ textColor }
        textPosition={ textPosition }
      />
    );
  }

  return (
    <div className='Banner'
      ref={ bannerRef }
    >
      { alignToGrid &&
      <GridContainer>
        { bannerContent }
      </GridContainer>
      }
      { !alignToGrid &&
         bannerContent
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef Banner
 * @type {object}
 * @property {object} actionGroups - actionGroups
 * @property {boolean} alignToGrid - alignToGrid
 * @property {string} body - body
 * @property {array} bulletedList - bulletedList
 * @property {string} eyebrow - eyebrow
 * @property {string} headline - title text of the Banner body
 * @property {string} headlineTag - headline tag of the Banner body
 * @property {object} media - media
 * @property {object} tag - tag
 * @property {string} textColor - textColor
 * @property {string} textPosition - textPosition
 * @property {string} variant - sets the variant of the Banner
 */

const propTypes = {
  actionGroups: PropTypes.object,
  alignToGrid: PropTypes.bool,
  body: PropTypes.string,
  bulletedList: PropTypes.array,
  eyebrow: PropTypes.string,
  headline: PropTypes.string,
  headlineTag: PropTypes.string,
  tag: PropTypes.object,
  textColor: PropTypes.oneOf( ['content-default', 'content-knockout'] ),
  textPosition: PropTypes.oneOf( ['top-left', 'center-left', 'bottom-left', 'center', 'right'] ),
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} headlineTag - headlineTag: 'h2' - sets the default for the headline tag
 * @property {string} textPosition - textPosition: 'top-left' - sets the default.
 */
export const defaultProps =  {
  headlineTag: 'h2',
  textPosition: 'top-left'
};

Banner.propTypes = propTypes;
Banner.defaultProps = defaultProps;

export default Banner;